import isEqual from 'lodash/isEqual';

import {
  FlattenedLocationItem,
  GetLocationStateParams,
  LocationDisplayState,
  NestedLocation,
  NestedLocationIndex,
  NestedLocationSelectable,
  NestedLocationWithChildren,
  ParentContext,
} from '../../../types/nestedLocations';
import { SelectedLocations, SelectedStatus } from '../utils/LocationsReducer';

export interface LocationsIndexes {
  locationById: Map<number, NestedLocation>;
  childrenByParentId: Map<number, number[]>;
}

export const checkIfLocationMatchesSearch = (location: NestedLocation, searchQuery: string) => {
  return (
    location.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    location.address?.toLowerCase().includes(searchQuery.toLowerCase())
  );
};

const buildLocationsTree = (
  flatLocations: NestedLocation[],
  parent_id: number = null,
): NestedLocationWithChildren[] => {
  const locations: NestedLocation[] = flatLocations.filter(
    (location) => location.parent_id === parent_id,
  );
  const remainingLocations = flatLocations.filter((location) => location.id !== parent_id);
  return locations.map((location: NestedLocation) => {
    return {
      ...location,
      children: buildLocationsTree(remainingLocations, location.id),
    };
  });
};

/** Builds the indexes for the locations to access them by id and by parent id efficiently. */
export const buildLocationIndexes = (flatLocations: NestedLocation[]): LocationsIndexes => {
  const locationsTree = buildLocationsTree(flatLocations);

  const locationById = new Map<number, NestedLocation>();
  const childrenByParentId = new Map<number, number[]>();

  const buildIndexRecursive = (currentLocation: NestedLocationWithChildren, depth = 0) => {
    const { children, ...locationToSet } = currentLocation;
    locationById.set(currentLocation.id, { ...locationToSet, depth });

    // Add children to childrenByParentId
    return children.forEach((childLocation) => {
      const newChildren = [...(childrenByParentId.get(currentLocation.id) || []), childLocation.id];
      childrenByParentId.set(currentLocation.id, newChildren);
      buildIndexRecursive(childLocation, depth + 1);
    });
  };

  locationsTree.forEach((location) => buildIndexRecursive(location));

  return { locationById, childrenByParentId };
};

/**
 * Builds an enhanced index with allowedLocations and all their ancestors.
 * Ancestors not in allowedLocations are marked as non-selectable.
 * Only includes locations that are either allowed or ancestors of allowed locations.
 */
export const buildPreloadedLocationSelectorData = (
  allLocationsById: Map<number, NestedLocation>,
  searchQuery: string,
  allowedLocations?: NestedLocation[],
) => {
  const permittedLocations = allowedLocations || Array.from(allLocationsById.values());
  const allowedLocationIds = new Set(
    permittedLocations
      .filter((loc) => checkIfLocationMatchesSearch(loc, searchQuery))
      .map((loc) => loc.id),
  );
  const relevantLocationIds = new Set<number>();

  allowedLocationIds.forEach((locationId) => {
    relevantLocationIds.add(locationId);
    getLocationAncestors(locationId, allLocationsById).forEach((ancestor) => {
      relevantLocationIds.add(ancestor.id);
    });
  });

  const relevantLocations = Array.from(allLocationsById.values()).filter((location) =>
    relevantLocationIds.has(location.id),
  );

  const locationById = new Map<number, NestedLocationSelectable>();
  const childrenByParentId = new Map<number, number[]>();

  const buildSelectableIndexRecursive = (
    currentLocation: NestedLocationWithChildren,
    depth = 0,
  ) => {
    const { children, ...locationToSet } = currentLocation;
    const isSelectable = allowedLocationIds.has(currentLocation.id);

    locationById.set(currentLocation.id, {
      ...locationToSet,
      depth,
      selectable: isSelectable,
    });

    children.forEach((childLocation) => {
      const newChildren = [...(childrenByParentId.get(currentLocation.id) || []), childLocation.id];
      childrenByParentId.set(currentLocation.id, newChildren);
      buildSelectableIndexRecursive(childLocation, depth + 1);
    });
  };

  const locationsTree = buildLocationsTree(relevantLocations);
  locationsTree.forEach((location) => buildSelectableIndexRecursive(location));

  return { locationById, childrenByParentId };
};

/** Returns the ancestors (parents) of a location. */
export const getLocationAncestors = (locationId: number, locationById: NestedLocationIndex) => {
  const locationAncestors = [];
  const initialLocation = locationById.get(locationId);

  let currentLocationId = initialLocation?.parent_id;
  while (currentLocationId) {
    const location = locationById.get(currentLocationId);
    locationAncestors.push(location);
    currentLocationId = location.parent_id;
  }
  return locationAncestors;
};

/** Checks if a location should be marked as selected. */
const checkIfLocationIsSelected = (
  locationId: number,
  selectedLocations: SelectedLocations,
  locationById: NestedLocationIndex,
  childrenByParentId: Map<number, number[]>,
) => {
  const locationAncestors = getLocationAncestors(locationId, locationById);

  const anyAncestorSelected = locationAncestors.some((ancestor) => {
    return ['selected', 'onlyChildren'].includes(selectedLocations.get(ancestor.id));
  });

  const children = getLocationChildren(locationId, childrenByParentId, locationById);
  const anyChildExcluded = children.some((child) =>
    ['excluded', 'onlyChildren'].includes(selectedLocations.get(child.id)),
  );

  const allChildrenSelected = children.every(
    (child) => selectedLocations.get(child.id) === 'selected',
  );

  const currentLocationSelectedStatus = selectedLocations.get(locationId);

  return (
    ((anyAncestorSelected && currentLocationSelectedStatus !== 'excluded') ||
      allChildrenSelected ||
      currentLocationSelectedStatus === 'onlyChildren') &&
    !anyChildExcluded
  );
};

/** Changes inplace the selection status of all ancestors or parents of a location. */
export const changeAncestorsSelectionStatus = (
  locationId: number,
  selectedLocations: SelectedLocations,
  locationById: NestedLocationIndex,
  childrenByParentId: Map<number, number[]>,
) => {
  const locationShouldBeSelected = checkIfLocationIsSelected(
    locationId,
    selectedLocations,
    locationById,
    childrenByParentId,
  );
  const location = locationById.get(locationId);

  if (locationShouldBeSelected && location) {
    selectedLocations.set(locationId, 'selected');
  }

  if (location?.parent_id) {
    changeAncestorsSelectionStatus(
      location.parent_id,
      selectedLocations,
      locationById,
      childrenByParentId,
    );
  }
};

/** Removes inplace the selection status of all children of a location (Not unselect children). */
export const clearChildrenSelectStatus = (
  locationId: number,
  locationById: NestedLocationIndex,
  childrenByParentId: Map<number, number[]>,
  selectedLocations: SelectedLocations,
) => {
  const childrenIds = childrenByParentId.get(locationId) || [];
  childrenIds.forEach((childId) => {
    const child = locationById.get(childId);
    if (child) {
      selectedLocations.delete(child.id);
      clearChildrenSelectStatus(childId, locationById, childrenByParentId, selectedLocations);
    }
  });
};

/** Used to unselect the selection status of all ancestors or parents of a location.
 * This is change the selection status of the ancestors to onlyChildren until a unselected ancestor is found.
 */
export const unselectLocationAncestors = (
  locationId: number,
  selectedLocations: SelectedLocations,
  locationById: NestedLocationIndex,
) => {
  const parents = getLocationAncestors(locationId, locationById);
  let hasOnlyChildren = false;
  parents.reverse().forEach((parent) => {
    if (selectedLocations.get(parent.id) === 'selected' || hasOnlyChildren) {
      selectedLocations.set(parent.id, 'onlyChildren');
      hasOnlyChildren = true;
    }
  });
};

/** Returns the children sublocations of a location. */
export const getLocationChildren = (
  locationId: number,
  childrenByParentId: Map<number, number[]>,
  locationById: NestedLocationIndex,
) => {
  const childrenIds = childrenByParentId.get(locationId) || [];
  return childrenIds.map((childId) => locationById.get(childId));
};

/** Get the selected children count of a location. */
export const getSelectedChildrenCount = (
  location: NestedLocation,
  childrenByParentId: Map<number, number[]>,
  locationById: NestedLocationIndex,
  selectedLocations: SelectedLocations,
  allLocationsSelected: boolean,
  multipleSelection: boolean,
  autoSelectChildren: boolean,
) => {
  if (!multipleSelection) {
    return 0;
  }

  const children = getLocationChildren(location.id, childrenByParentId, locationById);
  const locationSelectedStatus = selectedLocations.get(location.id);

  if (children.length === 0) return 0;

  if (!autoSelectChildren) {
    return children.filter((child) => selectedLocations.get(child.id) === 'selected').length;
  }

  if (['selected', 'onlyChildren'].includes(locationSelectedStatus) || allLocationsSelected) {
    const excludedChildren = children.filter((child) =>
      ['excluded', 'onlyChildren'].includes(selectedLocations.get(child.id)),
    );
    return children.length - excludedChildren.length;
  }

  const ancestors = getLocationAncestors(location.id, locationById);
  const ancestorsSelected = ancestors.some((ancestor) =>
    ['selected', 'onlyChildren'].includes(selectedLocations.get(ancestor.id)),
  );

  if (ancestorsSelected || allLocationsSelected) {
    return children.length;
  }

  const selectedChildren = children.filter(
    (child) => selectedLocations.get(child.id) === 'selected',
  );
  return selectedChildren.length;
};

/** Checks if a location should be force expanded in case the search query is not empty. */
export const checkIfLocationIsForceExpanded = (
  searchQuery: string,
  location: NestedLocation,
  parentMatchesSearch: boolean,
) => {
  const matchesSearch = checkIfLocationMatchesSearch(location, searchQuery);
  return (
    searchQuery.length > 0 &&
    location.expanded === undefined &&
    !matchesSearch &&
    !parentMatchesSearch
  );
};

export const calculateLocationState = ({
  location,
  searchQuery,
  selectedLocations,
  locationsIndex,
  parentContext,
  multipleSelection,
  expandedLocations,
  allLocationsSelected,
  autoSelectChildren,
}: GetLocationStateParams): LocationDisplayState => {
  const isExpanded =
    expandedLocations[location.id] ||
    checkIfLocationIsForceExpanded(searchQuery, location, parentContext.matchesSearch);
  const selectedStatus = selectedLocations.get(location.id);
  const isSelected =
    selectedStatus === 'selected' ||
    (multipleSelection &&
      autoSelectChildren &&
      parentContext.shouldSelectChildren &&
      !['excluded', 'onlyChildren'].includes(selectedStatus));
  const matchesSearch = checkIfLocationMatchesSearch(location, searchQuery);
  const isHighlighted = searchQuery.length > 0 ? matchesSearch : parentContext.isHighlighted;
  const children = getLocationChildren(
    location.id,
    locationsIndex.childrenByParentId,
    locationsIndex.locationById,
  );
  const selectedChildrenCount = getSelectedChildrenCount(
    location,
    locationsIndex.childrenByParentId,
    locationsIndex.locationById,
    selectedLocations,
    allLocationsSelected,
    multipleSelection,
    autoSelectChildren,
  );

  return {
    isExpanded,
    isSelected,
    matchesSearch,
    isHighlighted,
    children,
    selectedChildrenCount,
  };
};

export const hasSameItems = (a: number[], b: number[]) => {
  return isEqual([...a].sort(), [...b].sort());
};

export const convertSelectedLocationsToObject = (
  selectedLocations: Map<number, SelectedStatus>,
) => {
  return Object.fromEntries(selectedLocations.entries());
};

export const convertSelectedLocationsToMap = (
  selectedLocations: Record<string, SelectedStatus>,
) => {
  return new Map(Object.entries(selectedLocations).map(([key, value]) => [Number(key), value]));
};

/**
 * Optimizes the selected children count for locations that are ancestors of the selected location.
 * In single selection mode, ancestors should show a count of 1 instead of the full count.
 */
export const optimizeSelectedChildrenCount = (
  selectedChildrenCount: number,
  locationId: number,
  selectedLocationAncestors: number[],
): number => {
  if (selectedLocationAncestors.includes(locationId)) {
    return 1;
  }
  return selectedChildrenCount;
};

/**
 * Determines if a parent location is selected based on its state and selection status.
 * A parent is considered selected if it's marked as selected or if only its children are selected.
 */
export const childrenShouldBeSelected = (
  isSelected: boolean,
  locationId: number,
  selectedLocations: SelectedLocations,
): boolean => {
  return isSelected || selectedLocations.get(locationId) === 'onlyChildren';
};

/**
 * Checks if a location should be skipped during flattening.
 * Locations in the blacklist are excluded from the tree.
 */
export const shouldSkipLocation = (locationId: number, locationsBlacklist?: number[]): boolean => {
  return locationsBlacklist?.includes(locationId) ?? false;
};

/**
 * Determines if children should be processed and added to the flattened list.
 * Children are only processed if the location is expanded and has children.
 */
export const shouldProcessChildren = (isExpanded: boolean, childrenCount: number): boolean => {
  return isExpanded && childrenCount > 0;
};

/**
 * Get an array of nested locations based on a selectedLocations record.
 * Useful when we selected a set of locations and we want to get them from the locationById index.
 */
export const filterLocationsBySelectedLocations = (
  locationById: NestedLocationIndex,
  childrenByParentId: Map<number, number[]>,
  selectedLocations: Record<string, SelectedStatus>,
  allLocationsSelected: boolean,
) => {
  const recursiveFilterSelectedLocation = (location: NestedLocation, parentIsSelected: boolean) => {
    const filteredLocations: Map<number, NestedLocation> = new Map();

    const isSelectedByAllLocations =
      allLocationsSelected && selectedLocations?.[location.id] !== 'excluded';

    const isSelectedByParent =
      parentIsSelected && !['excluded', 'onlyChildren'].includes(selectedLocations[location.id]);
    const isSelected = selectedLocations[location.id] === 'selected';

    let childrenSelected = false;
    if (isSelectedByParent || isSelected || isSelectedByAllLocations) {
      filteredLocations.set(location.id, location);
      childrenSelected = true;
    } else if (selectedLocations[location.id] === 'onlyChildren') {
      childrenSelected = true;
    }

    childrenByParentId.get(location.id)?.forEach((childId) => {
      const child = locationById.get(childId);
      const selectedChildren = recursiveFilterSelectedLocation(child, childrenSelected);
      selectedChildren.forEach((child) => filteredLocations.set(child.id, child));
    });

    return filteredLocations;
  };

  const rootLocations = Array.from(locationById.values()).filter(
    (location) => location.parent_id === null,
  );

  const filteredLocations: Map<number, NestedLocation> = new Map();
  rootLocations.forEach((location) => {
    const selectedLocations = recursiveFilterSelectedLocation(location, false);
    selectedLocations.forEach((location) => filteredLocations.set(location.id, location));
  });
  return Array.from(filteredLocations.values());
};

interface GetFlattenedLocationsParams {
  rootLocations: NestedLocation[];
  searchQuery: string;
  selectedLocations: SelectedLocations;
  expandedLocations: Record<number, boolean>;
  locationsIndex: LocationsIndexes;
  allLocationsSelected: boolean;
  multipleSelection: boolean;
  autoSelectChildren: boolean;
  selectedLocationAncestors: number[];
  locationsBlacklist?: number[];
}

const ROOT_LOCATION_LEVEL = 0;

/**
 * Flattens nested locations into a flat array for virtualization.
 * Processes locations recursively, applying search, selection, and expansion logic.
 */
export const getFlattenedLocations = ({
  rootLocations,
  searchQuery,
  selectedLocations,
  expandedLocations,
  locationsIndex,
  allLocationsSelected,
  multipleSelection,
  autoSelectChildren,
  selectedLocationAncestors,
  locationsBlacklist,
}: GetFlattenedLocationsParams): FlattenedLocationItem[] => {
  const flattened: FlattenedLocationItem[] = [];

  const flattenRecursive = (
    locations: NestedLocation[],
    level: number,
    parentContext: ParentContext,
  ) => {
    locations.forEach((location) => {
      // 1. Skip blacklisted locations early
      if (shouldSkipLocation(location.id, locationsBlacklist)) {
        return;
      }

      // 2. Calculate the display state for this location
      const state = calculateLocationState({
        location,
        searchQuery,
        selectedLocations,
        expandedLocations,
        locationsIndex,
        parentContext,
        allLocationsSelected,
        multipleSelection,
        autoSelectChildren,
      });

      // 3. Optimize the selected children count for ancestors in single selection mode
      const optimizedCount = optimizeSelectedChildrenCount(
        state.selectedChildrenCount,
        location.id,
        selectedLocationAncestors,
      );

      // 4. Determine if this location acts as a selected parent for its children
      const shouldSelectChildren = childrenShouldBeSelected(
        state.isSelected,
        location.id,
        selectedLocations,
      );

      // 5. Add the flattened item to the list
      flattened.push({
        location,
        level,
        isSelected: state.isSelected,
        isExpanded: state.isExpanded,
        isHighlighted: state.isHighlighted,
        selectedChildrenCount: optimizedCount,
        childrenCount: state.children.length,
        shouldSelectChildren,
        isRootLocation: level === 0,
      });

      // 6. Recursively process children if needed
      if (shouldProcessChildren(state.isExpanded, state.children.length)) {
        flattenRecursive(state.children, level + 1, {
          shouldSelectChildren,
          isHighlighted: state.isHighlighted,
          matchesSearch: state.matchesSearch || parentContext.matchesSearch,
        });
      }
    });
  };

  // Start flattening from root locations
  flattenRecursive(rootLocations, ROOT_LOCATION_LEVEL, {
    shouldSelectChildren: allLocationsSelected,
    isHighlighted: false,
    matchesSearch: false,
  });

  return flattened;
};

/** Returns all root locations (locations without parent). */
export const getRootLocations = (locationById: NestedLocationIndex) => {
  return Array.from(locationById.values()).filter((location) => location.parent_id === null);
};

/** Returns all siblings of a location (locations with the same parent). */
const getLocationSiblings = (
  location: NestedLocation,
  childrenByParentId: Map<number, number[]>,
  locationById: NestedLocationIndex,
) => {
  const siblingIds = childrenByParentId.get(location.parent_id) || [];
  return siblingIds
    .filter((siblingId) => siblingId !== location.id)
    .map((siblingId) => locationById.get(siblingId))
    .filter(Boolean);
};

/**
 * Handles the exclusion of a location when allLocationsSelected is true.
 * Selects all siblings and root locations except the excluded one,
 * and marks ancestors as 'onlyChildren'.
 */
export const handleExcludeLocationFromAllSelected = (
  location: NestedLocation,
  selectedLocations: SelectedLocations,
  locationById: NestedLocationIndex,
  childrenByParentId: Map<number, number[]>,
) => {
  const siblings = getLocationSiblings(location, childrenByParentId, locationById);
  const rootLocations = getRootLocations(locationById);

  [...siblings, ...rootLocations].forEach((locationToSelect) => {
    if (locationToSelect.id !== location.id) {
      selectedLocations.set(locationToSelect.id, 'selected');
    }
  });

  getLocationAncestors(location.id, locationById).forEach((ancestor) => {
    selectedLocations.set(ancestor.id, 'onlyChildren');
  });
};

/**
 * Checks if all root locations are selected and updates allLocationsSelected accordingly.
 * If all root locations are selected, clears the selectedLocations map.
 */
export const checkAndUpdateAllLocationsSelected = (
  selectedLocations: SelectedLocations,
  locationById: NestedLocationIndex,
): boolean => {
  const rootLocations = getRootLocations(locationById);
  const allRootLocationsSelected = rootLocations.every((rootLocation) => {
    return selectedLocations.get(rootLocation.id) === 'selected';
  });

  if (allRootLocationsSelected) {
    selectedLocations.clear();
    return true;
  }

  return false;
};
